import { Events } from '../events';

type WorkspaceMode = 'edit' | 'render';

// The editing aids Render mode switches off, remembered so returning to Edit restores the
// editor exactly as the user left it.
type EditAids = {
    grid: boolean;
    bound: boolean;
    overlay: boolean;
    tool: string | null;
};

// Edit is the editor as it has always been. Render hides the editing chrome and aids - left
// column, dock, toolbars, gizmos, grid, bound and splat overlay - leaving a clean view to
// review and capture. Chrome is hidden by CSS (workspace.scss); aids are switched off here.
const registerWorkspaceMode = (events: Events) => {
    let mode: WorkspaceMode = 'edit';
    let saved: EditAids | null = null;

    const setMode = (next: WorkspaceMode) => {
        if (next === mode) {
            return;
        }

        if (next === 'render') {
            saved = {
                grid: !!events.invoke('grid.visible'),
                bound: !!events.invoke('camera.bound'),
                overlay: !!events.invoke('camera.overlay'),
                tool: events.invoke('tool.active') ?? null
            };

            mode = next;
            events.fire('tool.deactivate');
            events.fire('grid.setVisible', false);
            events.fire('camera.setBound', false);
            events.fire('camera.setOverlay', false);
        } else {
            mode = next;

            if (saved) {
                events.fire('grid.setVisible', saved.grid);
                events.fire('camera.setBound', saved.bound);
                events.fire('camera.setOverlay', saved.overlay);
                if (saved.tool) {
                    events.fire(`tool.${saved.tool}`);
                }
                saved = null;
            }
        }

        document.body.classList[mode === 'render' ? 'add' : 'remove']('workspace-render');
        events.fire('workspace.mode', mode);
    };

    events.function('workspace.mode', () => mode);

    // the Edit-mode snapshot Render is currently hiding, so callers (e.g. docSerialize.view)
    // can report the user's real settings instead of the values Render forced off.
    events.function('workspace.editSnapshot', () => saved);

    events.on('workspace.setMode', (next: WorkspaceMode) => {
        setMode(next);
    });

    // Render mode has no editing tools: a tool shortcut pressed there would bring a gizmo back.
    events.on('tool.activated', (toolName: string | null) => {
        if (mode === 'render' && toolName) {
            events.fire('tool.deactivate');
        }
    });
};

export { registerWorkspaceMode };
export type { WorkspaceMode };
